import React, { useMemo, useState, useRef, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { useParams } from "react-router-dom";
import { marked } from "marked";
import DOMPurify from "dompurify";
import Cookies from "js-cookie";
import api from "../../../../../chat/api";
import ProposalEditor, { NodeActions } from "../../ProposalEditor";
import Tooltip from "../../../Project Page/Tooltip";
import { SAMPLE_DRAFT_SECTIONS } from "./draftData";
import SourcesListModal from "../../SourcesListModal";
import SourceViewModal from "../../SourceViewModal";

// The model often wraps the draft in a ```json fence and/or a {"body": "..."}
// envelope; mid-stream we strip whatever prefix has arrived so far and
// unescape the JSON string so the preview reads as prose, not raw JSON.
const cleanStreamText = (raw) => {
  let t = (raw || "").replace(/^\s*```(?:json)?\s*/i, "");
  const wrapper = t.match(/^\s*\{\s*"body"\s*:\s*"/);
  if (wrapper) {
    t = t
      .slice(wrapper[0].length)
      .replace(/"\s*\}?\s*(```)?\s*$/, "")
      .replace(/\\n/g, "\n")
      .replace(/\\t/g, "\t")
      .replace(/\\"/g, '"')
      .replace(/\\\\/g, "\\");
  }
  return t;
};

// Between the "Drafting the response…" frame (~70–75%) and the first streamed
// token, the model is silently processing the prompt — nothing arrives for a
// while. Cycle plausible "thinking" lines and creep the percent so the wait
// reads as activity, not a hang. Purely cosmetic; real frames take over the
// moment streaming starts.
const THINKING_LINES = [
  "Reviewing what's already written in your proposal…",
  "Weighing evidence from your knowledge base…",
  "Structuring this section's outline…",
  "Aligning the draft with your win theme…",
  "Selecting the strongest supporting points…",
  "Composing the opening…",
  "Polishing phrasing before writing it out…",
];

// Step tracker for the generation stages — checkmarks are truthful and always
// move forward, unlike a percentage that sits frozen during the long LLM call.
// Thresholds map to the backend's real progress frames (generate: 5/15/30/
// 55–65/75; regenerate: 10/45–60/70).
const GEN_STEPS = [
  { label: "Reading", at: 0 },
  { label: "Planning", at: 30 },
  { label: "Evidence", at: 45 },
  { label: "Writing", at: 70 },
];

const GenStepTracker = ({ percent }) => {
  if (typeof percent !== "number") return null;
  return (
    <div className="flex items-center gap-2 font-outfit text-[13px]">
      {GEN_STEPS.map((s, i) => {
        const next = GEN_STEPS[i + 1];
        const done = next ? percent >= next.at : false;
        const active = !done && percent >= s.at;
        return (
          <React.Fragment key={s.label}>
            {i > 0 && (
              <span
                className={`h-px w-5 ${
                  percent >= s.at ? "bg-[#A04A97]" : "bg-gray-200"
                }`}
              />
            )}
            <span
              className={`flex items-center gap-1.5 whitespace-nowrap ${
                done
                  ? "text-[#A04A97] font-medium"
                  : active
                  ? "text-[#A04A97] font-semibold"
                  : "text-gray-400"
              }`}
            >
              {/* Fixed-width marker so the dot and the tick occupy the same
                  space — labels stay put instead of nudging as steps complete. */}
              <span className="w-3 flex items-center justify-center shrink-0">
                {done ? (
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                ) : (
                  <span
                    className={`block w-1.5 h-1.5 rounded-full ${
                      active ? "bg-[#A04A97] animate-pulse" : "bg-gray-300"
                    }`}
                  />
                )}
              </span>
              {s.label}
            </span>
          </React.Fragment>
        );
      })}
    </div>
  );
};

// Current status line: the backend's real message until the Writing stage,
// then cycling "thinking" lines to fill the silent gap before the first token.
const GenStatusMessage = ({ progress, center = false }) => {
  const percent = progress?.percent;
  const writing = typeof percent === "number" && percent >= 70;
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!writing) return undefined;
    const t = setInterval(() => setTick((p) => p + 1), 4000);
    return () => clearInterval(t);
  }, [writing]);
  const line = writing
    ? THINKING_LINES[tick % THINKING_LINES.length]
    : progress?.message || "Generating content…";
  return (
    <p
      key={line}
      className={`text-[#3F3F46] font-outfit text-[15px] font-medium ${
        center ? "text-center max-w-[420px]" : "truncate"
      } ${writing ? "animate-pulse" : ""}`}
    >
      {line}
    </p>
  );
};

// Live view of the response as the LLM writes it. Deltas arrive in throttled
// ~0.6s chunks; a typewriter reveal smooths them into fluent writing. Auto-
// follows the tail unless the user scrolls up, with a jump-back pill.
const StreamingPreview = ({ text }) => {
  const scrollRef = useRef(null);
  const [pinned, setPinned] = useState(true);
  const cleaned = useMemo(() => cleanStreamText(text) || "", [text]);

  // Reveal towards the full text in small steps (faster when far behind, so
  // it always catches up before the next chunk lands).
  const [shown, setShown] = useState(0);
  useEffect(() => {
    if (shown >= cleaned.length) return undefined;
    const t = setInterval(() => {
      setShown((s) =>
        Math.min(cleaned.length, s + Math.max(4, Math.ceil((cleaned.length - s) / 10)))
      );
    }, 80);
    return () => clearInterval(t);
  }, [cleaned.length, shown]);

  const html = useMemo(
    () => DOMPurify.sanitize(marked.parse(cleaned.slice(0, shown))),
    [cleaned, shown]
  );
  useEffect(() => {
    const el = scrollRef.current;
    if (el && pinned) el.scrollTop = el.scrollHeight;
  }, [html, pinned]);

  return (
    <div className="relative flex-1 min-h-0 flex flex-col">
      <div
        ref={scrollRef}
        onScroll={(e) => {
          const el = e.currentTarget;
          setPinned(el.scrollHeight - el.scrollTop - el.clientHeight < 40);
        }}
        className="flex-1 min-h-0 overflow-y-auto px-8 py-5"
      >
        <div
          className="prose max-w-none font-outfit text-[15px] leading-relaxed text-[#27272A] [&_h1]:text-xl [&_h2]:text-lg [&_h3]:text-base"
          dangerouslySetInnerHTML={{ __html: html }}
        />
        <span className="inline-block w-[7px] h-[15px] bg-[#A04A97] animate-pulse" />
      </div>
      {!pinned && (
        <button
          onClick={() => {
            setPinned(true);
            const el = scrollRef.current;
            if (el) el.scrollTop = el.scrollHeight;
          }}
          className="absolute bottom-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-[#A04A97] text-white text-[12px] font-outfit shadow-md"
        >
          ↓ Jump to latest
        </button>
      )}
    </div>
  );
};

// Persist the active section per project so a refresh restores it.
const ACTIVE_SECTION_PREFIX = "proposal_draft_active_";
const readSavedActiveId = (projectId) => {
  if (!projectId) return null;
  try {
    return localStorage.getItem(`${ACTIVE_SECTION_PREFIX}${projectId}`);
  } catch {
    return null;
  }
};
const writeSavedActiveId = (projectId, id) => {
  if (!projectId || !id) return;
  try {
    localStorage.setItem(`${ACTIVE_SECTION_PREFIX}${projectId}`, id);
  } catch {
    /* storage unavailable — non-fatal */
  }
};

// GFM + single newlines → <br>; embedded raw HTML passes through untouched.
marked.setOptions({ gfm: true, breaks: true, async: false });

// Matched only at line start, so they can't false-positive on saved editor HTML.
// No inline `**bold**` marker: it appears as literal text in saved HTML.
const MD_MARKERS = [
  /(^|\n)[ \t]{0,3}#{1,6}\s+\S/, // ATX headings (#### …)
  /(^|\n)[ \t]{0,3}[*\-•·]\s+\S/, // bullet list (incl. the • / · glyphs)
  /(^|\n)[ \t]{0,3}\d+\.\s+\S/, // ordered list
  /(^|\n)[ \t]{0,3}\|.+\|/, // pipe table row
];
const looksLikeMarkdown = (s) => MD_MARKERS.some((re) => re.test(s));

// Model bullets with "•"/"·" glyphs, which Markdown doesn't treat as list
// markers; normalize line-leading glyphs to "- ".
const normalizeMarkdown = (s) => s.replace(/^([ \t]*)[•·][ \t]+/gm, "$1- ");

// API `response` is Markdown (sometimes with embedded HTML) → sanitized editor
// HTML. Content that's already editor HTML with no Markdown markers is left
// untouched to avoid double-parsing.
export const toEditorHtml = (raw) => {
  const s = String(raw ?? "");
  if (!s.trim()) return "";

  // Pure editor HTML (no Markdown markers) → leave as-is.
  if (
    !looksLikeMarkdown(s) &&
    /<(h[1-6]|p|ul|ol|table|div|blockquote|pre)[\s>]/i.test(s)
  ) {
    return s;
  }

  // Parse Markdown (preserving raw HTML like <table>) then sanitize. A parse
  // failure must never blank the editor, so fall back to raw/escaped text.
  try {
    const dirty = marked.parse(normalizeMarkdown(s));
    return DOMPurify.sanitize(dirty, { USE_PROFILES: { html: true } });
  } catch (err) {
    console.error("toEditorHtml: markdown parse failed, using raw content", err);
    // If it had HTML tags, keep them; otherwise sanitize the plain string.
    return /<[a-z][\s\S]*>/i.test(s)
      ? DOMPurify.sanitize(s, { USE_PROFILES: { html: true } })
      : DOMPurify.sanitize(`<p>${s}</p>`, { USE_PROFILES: { html: true } });
  }
};

// Completion status from content (API shape) or explicit `status` (sample shape).
const deriveStatus = (node) => {
  if (node.status) return node.status;
  const hasContent = !!(node.response || node.content);
  if (node.children && node.children.length > 0) {
    const done = node.children.filter((c) => c?.response || c?.content).length;
    if (done === 0) return "not_started";
    if (done === node.children.length) return "done";
    return "in_progress";
  }
  return hasContent ? "done" : "not_started";
};

// Coerce the API `analysis.toc` or sample shape into one normalized list.
export const normalizeSections = (apiSections) => {
  if (!apiSections || apiSections.length === 0) return SAMPLE_DRAFT_SECTIONS;
  return [...apiSections]
    .filter(Boolean)
    .sort((a, b) => parseFloat(a?.number || 0) - parseFloat(b?.number || 0))
    .map((section, i) => ({
      // Always a unique id — without it every node shares `undefined` and all
      // highlight as "active" at once.
      id: String(section.id ?? section.number ?? `sec-${i}`),
      // Identifier the mark-as-done API matches on.
      targetId: String(section.id ?? section.number ?? ""),
      number: section.number,
      title: section.title,
      status: deriveStatus(section),
      markAsDone: !!section.mark_as_done,
      kbScope: section.kb_scope || { folders: [], files: [] },
      rfpScope: section.rfp_scope || { folders: [], files: [] },
      assignedMembers: section.assigned_members || [],
      // Query that produced this node — shown read-only in the regenerate modal.
      query: section.section_query || section.query || "",
      // Readable writing task (agentic TOCs) — previewed during generation
      // instead of the retrieval query when present.
      writingBrief: section.writing_brief || "",
      content: toEditorHtml(section.response || section.content || ""),
      sources: section.source || section.sources || [],
      children: (section.children || [])
        .filter(Boolean)
        .sort((a, b) => parseFloat(a?.number || 0) - parseFloat(b?.number || 0))
        .map((sub, j) => ({
          id: String(sub.id ?? sub.number ?? `sec-${i}-${j}`),
          targetId: String(sub.id ?? sub.number ?? ""),
          number: sub.number,
          title: sub.title,
          status: deriveStatus(sub),
          markAsDone: !!sub.mark_as_done,
          kbScope: sub.kb_scope || { folders: [], files: [] },
          rfpScope: sub.rfp_scope || { folders: [], files: [] },
          assignedMembers: sub.assigned_members || [],
          query: sub.subsection_query || sub.query || "",
          writingBrief: sub.writing_brief || "",
          content: toEditorHtml(sub.response || sub.content || ""),
          sources: sub.source || sub.sources || [],
        })),
    }));
};

// Strip trailing dot (API sometimes sends "1." which would render as "1..").
const formatNumber = (num) => String(num ?? "").replace(/\.+$/, "");

// Suppress the number prefix for unnumbered nodes (e.g. the Cover Letter).
// The API marks these with a "U" prefix on the number (e.g. "U1"), so hide any
// such index in the sidebar.
const displayNumber = (node) => {
  const num = formatNumber(node?.number);
  return /^U/i.test(num) ? "" : num;
};

const StatusDot = ({ status }) => {
  const color =
    status === "done"
      ? "bg-[#22C55E]"
      : status === "in_progress"
      ? "bg-[#A04A97]"
      : "bg-[#D1D5DB]";
  return <span className={`w-2 h-2 rounded-full flex-shrink-0 ${color}`} />;
};

// Shown in place of the status dot while a node is being generated.
const MiniSpinner = () => (
  <span className="w-3 h-3 rounded-full border-[1.5px] border-[#A04A97] border-t-transparent animate-spin flex-shrink-0" />
);

// Hover-revealed "+" divider to insert a new section at this position.
const AddSectionDivider = ({ onAdd }) => {
  if (!onAdd) return null;
  return (
    <div className="relative h-4 flex items-center justify-center opacity-0 group-hover/sec:opacity-100 transition-opacity">
      <div className="absolute inset-x-1 top-1/2 h-px bg-gray-200" />
      <button
        type="button"
        onClick={onAdd}
        title="Add section here"
        className="relative z-10 w-5 h-5 rounded-full border border-gray-300 bg-white text-gray-500 flex items-center justify-center hover:border-[#A04A97] hover:text-[#A04A97] transition-colors"
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
          <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
        </svg>
      </button>
    </div>
  );
};

// Hover-revealed "•••" action menu for a row. Rendered through a portal so the
// sidebar's scroll can't clip it; position refreshed on scroll/resize.
const DraftActionMenu = ({
  isSubsection = false,
  canAddSub = false,
  triggerClassName = "",
  onAddSub,
  onEdit,
  onDelete,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuStyles, setMenuStyles] = useState({});
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  const MENU_WIDTH = 180;

  const updatePosition = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    // Right-align the menu under the trigger, clamped inside the viewport.
    const left = Math.min(
      Math.max(8, rect.right - MENU_WIDTH),
      window.innerWidth - MENU_WIDTH - 8
    );
    // Estimate height (Add Sub-Section is optional) and flip the menu above the
    // trigger when there isn't enough room below — keeps lower rows' menus
    // fully visible instead of clipping past the viewport bottom.
    const estHeight = ((canAddSub ? 1 : 0) + 2) * 40 + 12;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUp = spaceBelow < estHeight + 8 && rect.top > spaceBelow;
    setMenuStyles({
      left: `${left}px`,
      width: `${MENU_WIDTH}px`,
      ...(openUp
        ? { bottom: `${window.innerHeight - rect.top + 4}px` }
        : { top: `${rect.bottom + 4}px` }),
    });
  }, [canAddSub]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClickOutside = (e) => {
      if (
        buttonRef.current &&
        !buttonRef.current.contains(e.target) &&
        menuRef.current &&
        !menuRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };
    const reposition = () => updatePosition();
    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [isOpen, updatePosition]);

  const toggle = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (!isOpen) updatePosition();
    setIsOpen((v) => !v);
  };

  // Close the menu and stop bubbling to the row's select handler.
  const run = (fn) => (e) => {
    e.stopPropagation();
    setIsOpen(false);
    fn?.(e);
  };

  const noun = isSubsection ? "Subsection" : "Section";
  const itemClass =
    "w-full text-left pl-3 pr-2 py-2 text-[14px] text-[#555555] hover:bg-gray-50 flex items-center gap-3 font-outfit transition-colors";

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={toggle}
        title={`${noun} actions`}
        className={`${triggerClassName} flex items-center justify-center rounded-md text-[#8B8B8B]  transition-opacity ${
          isOpen ? "opacity-100 " : ""
        }`}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
          <circle cx="5" cy="12" r="2" />
          <circle cx="12" cy="12" r="2" />
          <circle cx="19" cy="12" r="2" />
        </svg>
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={menuRef}
            style={menuStyles}
            className="fixed flex flex-col items-start self-stretch gap-[2px]   rounded-[6px] border border-[rgba(0,0,0,0.10)] bg-white shadow-[0_4px_20px_rgba(0,0,0,0.12)] z-[9999]"
          >
            {canAddSub && (
              <button type="button" onClick={run(onAddSub)} className={itemClass}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M12 5v14M5 12h14" stroke="#71717A" strokeWidth="2" strokeLinecap="round" />
                </svg>
                Add Sub-Section
              </button>
            )}

            <button type="button" onClick={run(onEdit)} className={itemClass}>
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
  <path d="M1.33333 10.6667H2.28333L8.8 4.15L7.85 3.2L1.33333 9.71667V10.6667ZM0.666667 12C0.477778 12 0.319444 11.9361 0.191667 11.8083C0.0638889 11.6806 0 11.5222 0 11.3333V9.71667C0 9.53889 0.0333333 9.36944 0.1 9.20833C0.166667 9.04722 0.261111 8.90556 0.383333 8.78333L8.8 0.383333C8.93333 0.261111 9.08056 0.166667 9.24167 0.1C9.40278 0.0333333 9.57222 0 9.75 0C9.92778 0 10.1 0.0333333 10.2667 0.1C10.4333 0.166667 10.5778 0.266667 10.7 0.4L11.6167 1.33333C11.75 1.45556 11.8472 1.6 11.9083 1.76667C11.9694 1.93333 12 2.1 12 2.26667C12 2.44444 11.9694 2.61389 11.9083 2.775C11.8472 2.93611 11.75 3.08333 11.6167 3.21667L3.21667 11.6167C3.09444 11.7389 2.95278 11.8333 2.79167 11.9C2.63056 11.9667 2.46111 12 2.28333 12H0.666667ZM8.31667 3.68333L7.85 3.2L8.8 4.15L8.31667 3.68333Z" fill="#71717A"/>
</svg>
              Edit Title
            </button>

            <button
              type="button"
              onClick={run(onDelete)}
              className="w-full text-left pl-3 pr-2 py-2 text-[14px] text-[#D35050] hover:bg-red-50 flex items-center gap-3 font-outfit transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="11" height="12" viewBox="0 0 11 12" fill="none">
                <path d="M2 12C1.63333 12 1.31944 11.8694 1.05833 11.6083C0.797222 11.3472 0.666667 11.0333 0.666667 10.6667V2C0.477778 2 0.319444 1.93611 0.191667 1.80833C0.0638889 1.68056 0 1.52222 0 1.33333C0 1.14444 0.0638889 0.986111 0.191667 0.858333C0.319444 0.730556 0.477778 0.666667 0.666667 0.666667H3.33333C3.33333 0.477778 3.39722 0.319444 3.525 0.191667C3.65278 0.0638889 3.81111 0 4 0H6.66667C6.85555 0 7.01389 0.0638889 7.14167 0.191667C7.26944 0.319444 7.33333 0.477778 7.33333 0.666667H10C10.1889 0.666667 10.3472 0.730556 10.475 0.858333C10.6028 0.986111 10.6667 1.14444 10.6667 1.33333C10.6667 1.52222 10.6028 1.68056 10.475 1.80833C10.3472 1.93611 10.1889 2 10 2V10.6667C10 11.0333 9.86944 11.3472 9.60833 11.6083C9.34722 11.8694 9.03333 12 8.66667 12H2ZM8.66667 2H2V10.6667H8.66667V2ZM4 9.33333C4.18889 9.33333 4.34722 9.26945 4.475 9.14167C4.60278 9.01389 4.66667 8.85556 4.66667 8.66667V4C4.66667 3.81111 4.60278 3.65278 4.475 3.525C4.34722 3.39722 4.18889 3.33333 4 3.33333C3.81111 3.33333 3.65278 3.39722 3.525 3.525C3.39722 3.65278 3.33333 3.81111 3.33333 4V8.66667C3.33333 8.85556 3.39722 9.01389 3.525 9.14167C3.65278 9.26945 3.81111 9.33333 4 9.33333ZM6.66667 9.33333C6.85555 9.33333 7.01389 9.26945 7.14167 9.14167C7.26944 9.01389 7.33333 8.85556 7.33333 8.66667V4C7.33333 3.81111 7.26944 3.65278 7.14167 3.525C7.01389 3.39722 6.85555 3.33333 6.66667 3.33333C6.47778 3.33333 6.31944 3.39722 6.19167 3.525C6.06389 3.65278 6 3.81111 6 4V8.66667C6 8.85556 6.06389 9.01389 6.19167 9.14167C6.31944 9.26945 6.47778 9.33333 6.66667 9.33333Z" fill="#E21919" fillOpacity="0.75" />
              </svg>
              Delete
            </button>
          </div>,
          document.body
        )}
    </>
  );
};

// Two-letter initials for an avatar bubble.
const getInitials = (name) =>
  (name || "")
    .trim()
    .split(/\s+/)
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2) || "?";

// Overlapping member avatars: up to `max` bubbles, the rest collapse into a
// "+N" badge with a hover tooltip.
const AssignedAvatars = ({ members = [], max = 3 }) => {
  if (!members.length) return null;
  const shown = members.slice(0, max);
  const extra = members.slice(max);
  return (
    <div className="flex items-center flex-shrink-0">
      {shown.map((m, idx) => (
        <div
          key={m.user_id ?? idx}
          className={`relative group/avatar ${idx !== 0 ? "-ml-2.5" : ""} hover:!z-[9999]`}
          style={{ zIndex: 20 - idx }}
        >
          <div className="w-7 h-7 rounded-full bg-[#C95EBE] flex items-center justify-center text-white font-outfit text-[11px] font-medium border-2 border-white transition-transform hover:scale-105 relative z-10">
            {getInitials(m.full_name || m.email)}
          </div>
          <div className="absolute top-[120%] left-1/2 -translate-x-1/2 hidden group-hover/avatar:block z-[99999] min-w-[max-content]">
            <div className="bg-white rounded-[10px] shadow-[0_4px_20px_rgba(0,0,0,0.15)] border border-gray-100 py-2 pl-3 pr-2 text-center whitespace-nowrap">
              <span className="font-outfit text-[#171717] font-medium text-[13px]">
                {m.full_name || m.email}
                {m.role && <span className="text-[#C95EBE] ml-1">({m.role})</span>}
              </span>
            </div>
          </div>
        </div>
      ))}
      {extra.length > 0 && (
        <div
          className="relative group/more -ml-2.5 hover:!z-[9999]"
          style={{ zIndex: 10 }}
        >
          <div className="w-7 h-7 rounded-full bg-white border border-[#C95EBE] flex items-center justify-center text-[#171717] font-outfit text-[11px] font-medium transition-transform hover:scale-105 relative z-10">
            +{extra.length}
          </div>
          <div className="absolute top-[120%] left-1/2 -translate-x-1/2 hidden group-hover/more:block z-[99999] min-w-[max-content]">
            <div className="bg-white rounded-[10px] shadow-[0_4px_20px_rgba(0,0,0,0.15)] border border-gray-100 p-1 flex flex-col gap-2 whitespace-nowrap">
              {extra.map((m, idx) => (
                <div
                  key={m.user_id ?? idx}
                  className="text-left font-outfit text-[#171717] font-medium text-[13px]"
                >
                  {m.full_name || m.email}
                  {m.role && <span className="text-[#C95EBE] ml-1">({m.role})</span>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Skeleton shown while loading, so stale fallback sections never flash.
const DraftLoadingState = () => (
  <div className="h-full flex bg-white rounded-[10px] border border-gray-200 shadow-sm overflow-hidden">
    <aside className="w-[300px] flex-shrink-0 border-r border-gray-200 flex flex-col">
      <div className="flex items-center justify-between px-5 pt-5 pb-3">
        <div className="h-5 w-24 rounded bg-gray-200 animate-pulse" />
        <div className="h-4 w-14 rounded bg-gray-200 animate-pulse" />
      </div>
      <div className="px-5 mb-3">
        <div className="h-3 w-28 rounded bg-gray-100 animate-pulse" />
      </div>
      <div className="pl-3 pr-2 space-y-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="flex items-center gap-2 px-2 py-2">
            <span className="w-2 h-2 rounded-full bg-gray-200 animate-pulse" />
            <div
              className="h-4 rounded bg-gray-200 animate-pulse"
              style={{ width: `${55 + ((i * 13) % 35)}%` }}
            />
          </div>
        ))}
      </div>
    </aside>
    <section className="flex-1 min-w-0 flex flex-col gap-4 p-6">
      <div className="h-6 w-1/2 rounded bg-gray-200 animate-pulse" />
      <div className="flex items-center gap-2">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="h-6 w-6 rounded bg-gray-200 animate-pulse" />
        ))}
      </div>
      <div className="space-y-3 mt-2">
        {[92, 98, 85, 95, 70, 90, 99, 60, 88].map((w, i) => (
          <div
            key={i}
            className="h-3.5 rounded bg-gray-200 animate-pulse"
            style={{ width: `${w}%` }}
          />
        ))}
      </div>
    </section>
  </div>
);

// Folder icon for the scope breakdown rows.
const FolderIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="16"
    viewBox="0 0 17 13"
    fill="none"
    className="flex-shrink-0"
  >
    <path
      d="M1.45833 12.5C1.05347 12.5 0.709167 12.3582 0.425417 12.0746C0.141806 11.7908 0 11.4465 0 11.0417V1.50646C0 1.1016 0.149861 0.749306 0.449583 0.449584C0.749306 0.149861 1.1016 0 1.50646 0H5.45833C5.65917 0 5.85229 0.0390277 6.03771 0.117083C6.22299 0.195 6.38403 0.302361 6.52083 0.439167L7.74833 1.66667H15.1363C15.3136 1.66667 15.4621 1.72653 15.5817 1.84625C15.7014 1.96583 15.7613 2.11431 15.7613 2.29167C15.7613 2.46903 15.7014 2.6175 15.5817 2.73708C15.4621 2.85681 15.3136 2.91667 15.1363 2.91667H4.29C3.47167 2.91667 2.76042 3.17681 2.15625 3.69708C1.55208 4.21736 1.25 4.87549 1.25 5.67146V10.9935C1.25 11.0523 1.26472 11.1003 1.29417 11.1377C1.32347 11.1752 1.36215 11.2073 1.41021 11.234L3.03208 5.81896C3.1325 5.49521 3.31729 5.23507 3.58646 5.03854C3.85576 4.84187 4.15493 4.74354 4.48396 4.74354H15.2019C15.7126 4.74354 16.1178 4.94653 16.4175 5.3525C16.7171 5.75847 16.7958 6.20132 16.6538 6.68104L15.2179 11.4648C15.1228 11.7777 14.9447 12.0285 14.6835 12.2171C14.4223 12.4057 14.1325 12.5 13.8142 12.5H1.45833Z"
      fill="#E6C12E"
    />
  </svg>
);

// Down caret that flips up when its folder is expanded.
const RowChevron = ({ open }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    className={`flex-shrink-0 text-[#9CA3AF] transition-transform ${open ? "rotate-180" : ""}`}
  >
    <path
      d="M6 9l6 6 6-6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Knowledge-scope pill. Complete-KB stays a tooltip-only badge (nothing to
// break down). Custom Scope becomes a clickable pill whose dropdown lists the
// exact folders and files the active node was generated against.
//
// The node's `kb_scope` is flat — `folders` holds only fully-selected folders,
// and `files` holds individually-picked files with NO parent-folder reference
// (so a partially-selected folder isn't recorded as a folder at all). To show a
// faithful, expandable folder→files view we fetch the live KB on open and
// reconcile it against the scope: a folder appears if it's fully selected (all
// its docs) or has any docs whose key is in `files`; the rest stay as loose
// files. Falls back to the flat list if the KB fetch fails.
const KbScopePill = ({
  kbScope,
  rfpScope,
  label,
  tooltip,
  hasCustomScope,
  kbHasScope = true,
  nodeKey,
}) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [kbData, setKbData] = useState(null); // {folders, looseFiles} | {error} | null
  const [expanded, setExpanded] = useState({});
  const wrapRef = useRef(null);

  const scopeFolderIds = useMemo(
    () => new Set((kbScope?.folders || []).map((f) => String(f.id))),
    [kbScope]
  );
  const scopeFileKeys = useMemo(
    () => new Set((kbScope?.files || []).map((f) => f.key).filter(Boolean)),
    [kbScope]
  );

  // Reset the cached breakdown when switching sections (different scope).
  useEffect(() => {
    setKbData(null);
    setOpen(false);
  }, [nodeKey]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Fetch the KB once the dropdown opens, then group the scope by folder. Only
  // when the node actually has a KB scope — a project-only scope skips this.
  useEffect(() => {
    if (!open || kbData || !kbHasScope) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const organization_id = Cookies.get("selected_org_id");
        const params = {};
        if (organization_id) params.organization_id = organization_id;
        const res = await api.get("/api/v1/rfp/knowledge-base/documents/", {
          params,
        });
        if (cancelled) return;
        const allFolders = Array.isArray(res.data?.folders) ? res.data.folders : [];
        const allLoose = Array.isArray(res.data?.results) ? res.data.results : [];

        const matchedKeys = new Set();
        const folders = [];
        allFolders.forEach((folder) => {
          const whole = scopeFolderIds.has(String(folder.id));
          const docs = (folder.documents || []).filter(
            (d) => whole || (d.key && scopeFileKeys.has(d.key))
          );
          docs.forEach((d) => d.key && matchedKeys.add(d.key));
          if (whole || docs.length) {
            folders.push({ id: folder.id, name: folder.name, docs, whole });
          }
        });

        const looseFiles = [];
        allLoose.forEach((f) => {
          if (f.key && scopeFileKeys.has(f.key)) {
            matchedKeys.add(f.key);
            looseFiles.push({ id: f.id, key: f.key, name: f.name });
          }
        });
        // Scope files we couldn't locate (deleted/moved) — keep them visible by
        // their stored name rather than silently dropping them.
        (kbScope?.files || []).forEach((f) => {
          if (f.key && !matchedKeys.has(f.key)) {
            looseFiles.push({ id: f.id, key: f.key, name: f.name });
          }
        });

        setKbData({ folders, looseFiles });
      } catch {
        if (!cancelled) setKbData({ error: true });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, kbData, kbHasScope, scopeFolderIds, scopeFileKeys, kbScope]);

  if (!hasCustomScope) {
    return (
      <Tooltip content={tooltip} placement="right">
        <span className="inline-block pl-3 pr-2 py-1 rounded-full border border-[#0D4B6E] bg-[#0369A10F] text-[#0D4B6E] font-outfit text-[13px] cursor-default">
          {label}
        </span>
      </Tooltip>
    );
  }

  const toggleExpand = (id) =>
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  // Plain (icon-less) file row — used for files inside an expanded folder and
  // for loose files, matching the reference design.
  const fileRow = (name, key, pad = "px-4", weight = "font-normal") => (
    <div
      key={key}
      className={`${pad} py-1 font-outfit text-[13px] ${weight} text-[rgba(0,0,0,0.70)] truncate`}
    >
      {name}
    </div>
  );

  // Fallback when the KB fetch fails — the original flat list (no counts/files).
  const flatList = () => {
    const folders = kbScope?.folders || [];
    const files = kbScope?.files || [];
    if (!folders.length && !files.length) {
      return (
        <p className="px-4 py-3 text-[#9CA3AF] font-outfit text-[14px]">
          No specific folders or files recorded.
        </p>
      );
    }
    return (
      <div className="flex flex-col">
        {folders.map((f) => (
          <div
            key={`folder-${f.id}`}
            className="flex items-center gap-3 px-3 py-2"
          >
            <FolderIcon />
            <span className="font-outfit text-[13px] font-medium text-[rgba(0,0,0,0.70)] truncate">
              {f.name}
            </span>
          </div>
        ))}
        {files.map((f) => fileRow(f.name, `file-${f.key || f.id}`, "px-4", "font-medium"))}
      </div>
    );
  };

  // One expandable folder row (folder name + "N Selected" + nested files).
  const folderRow = (key, name, files, count) => {
    const isOpen = !!expanded[key];
    return (
      <div
        key={key}
        className={`rounded-[10px] transition-colors ${
          isOpen ? "border border-[#A04A97]" : "border border-transparent"
        }`}
      >
        <button
          type="button"
          onClick={() => toggleExpand(key)}
          className={`w-full flex items-center gap-3 px-3 py-2 text-left ${
            isOpen ? "border-b border-[#D9D9D9]" : ""
          }`}
        >
          <FolderIcon />
          <span className="flex-1 min-w-0 font-outfit text-[13px] font-medium text-[rgba(0,0,0,0.70)] truncate">
            {name}
          </span>
          <span className="flex-shrink-0 px-3 py-0.5 rounded-full bg-[#F2F2F2] text-[#52525B] font-outfit text-[13px]">
            {count} Selected
          </span>
          <RowChevron open={isOpen} />
        </button>
        {isOpen && <div className="pb-1">{files}</div>}
      </div>
    );
  };

  // KB group body: loading → fallback flat list → grouped folders + loose files.
  const renderKbBody = () => {
    if (loading) {
      return (
        <div className="px-2 py-3 flex items-center gap-2 text-[#9CA3AF] font-outfit text-[14px]">
          <span className="w-3.5 h-3.5 rounded-full border-[1.5px] border-[#A04A97] border-t-transparent animate-spin" />
          Loading…
        </div>
      );
    }
    if (!kbData || kbData.error) return flatList();
    if (kbData.folders.length === 0 && kbData.looseFiles.length === 0) {
      return (
        <p className="px-2 py-3 text-[#9CA3AF] font-outfit text-[14px]">
          No specific folders or files recorded.
        </p>
      );
    }
    return (
      <div className="flex flex-col gap-1">
        {kbData.folders.map((folder) =>
          folderRow(
            `kb-${folder.id}`,
            folder.name,
            folder.docs.map((d) => fileRow(d.name, `doc-${d.id}`, "px-2")),
            folder.docs.length
          )
        )}
        {kbData.looseFiles.map((f) =>
          fileRow(f.name, `file-${f.key || f.id}`, "px-3", "font-medium")
        )}
      </div>
    );
  };

  // Whole-KB selection: a single, non-expandable row. An empty kb_scope always
  // means the entire knowledge base was used, so there's nothing to break down —
  // just show it as selected (expandable breakdown is reserved for a partial KB
  // pick, handled by renderKbBody).
  const renderCompleteKb = () => (
    <div className="flex items-center gap-3 px-3 py-2">
      <FolderIcon />
      <span className="flex-1 min-w-0 font-outfit text-[13px] font-medium text-[rgba(0,0,0,0.70)]">
        Complete Knowledge Base
      </span>
      <span className="flex-shrink-0 font-outfit text-[12px] text-[#9CA3AF]">
        All files &amp; folders
      </span>
    </div>
  );

  // Project group body: rendered straight from the stamped rfp_scope (folders
  // carry their files inline, so no fetch is needed).
  const renderRfpBody = () => {
    const folders = rfpScope?.folders || [];
    const files = rfpScope?.files || [];
    return (
      <div className="flex flex-col gap-1">
        {folders.map((folder) => {
          const folderFiles = folder.files || [];
          return folderRow(
            `rfp-${folder.id}`,
            folder.name,
            folderFiles.map((d) => fileRow(d.name, `rfp-doc-${d.id}`, "px-2")),
            folderFiles.length
          );
        })}
        {files.map((f) =>
          fileRow(f.name, `rfp-file-${f.key || f.id}`, "px-3", "font-medium")
        )}
      </div>
    );
  };

  const groupLabel = (text) => (
    <p className="px-1 pb-1 font-outfit text-[11px] font-semibold uppercase tracking-wide text-[#9CA3AF]">
      {text}
    </p>
  );

  const rfpHasScope =
    (rfpScope?.folders?.length || 0) + (rfpScope?.files?.length || 0) > 0;
  // The KB section is always shown (either the partial breakdown or the
  // "Complete Knowledge Base" row), so the two group labels appear whenever a
  // project section is also present.
  const showBothLabels = rfpHasScope;

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title={tooltip}
        className="inline-flex items-center gap-1 pl-3 pr-2 py-1 rounded-full border border-[#0D4B6E] bg-[#0369A10F] text-[#0D4B6E] font-outfit text-[13px] hover:bg-[#0369A11A] transition-colors"
      >
        {label}
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          className={`transition-transform ${open ? "rotate-180" : ""}`}
        >
          <path
            d="M6 9l6 6 6-6"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open && (
        <div className="absolute left-0 top-[calc(100%+8px)] z-30 w-[420px] max-h-[360px] overflow-y-auto custom-scrollbar rounded-[14px] border border-[rgba(0,0,0,0.08)] bg-white shadow-[0_8px_30px_rgba(0,0,0,0.12)] p-3">
          <div>
            {showBothLabels && groupLabel("Knowledge Base")}
            {kbHasScope ? renderKbBody() : renderCompleteKb()}
          </div>
          {rfpHasScope && (
            <div className="mt-2 pt-2 border-t border-gray-100">
              {showBothLabels && groupLabel("Project Files")}
              {renderRfpBody()}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ProposalDraftStep = ({
  apiSections,
  loading = false,
  onScopeClick,
  onActiveNodeChange,
  doneOverrides = {},
  generatingTargets = {},
  onSave,
  onAddSectionAt,
  onAddSubsection,
  onGenerateNode,
  onRegenerateNode,
  onMarkNodeDone,
  onAssignNode,
  onSaveToKbNode,
  onDeleteNode,
  onUpdateTitle,
  isReviewer = false,
  folderId,
}) => {
  const isNodeGenerating = useCallback(
    (node) => !!(node?.targetId && generatingTargets[node.targetId]),
    [generatingTargets]
  );
  // Done = API `mark_as_done`, or an optimistic override from this session.
  const isNodeDone = useCallback(
    (node) => (doneOverrides[node?.id] ?? node?.markAsDone) === true,
    [doneOverrides]
  );
  // Drives the status dot: green (done) / purple (has content) / grey (empty).
  const nodeStatus = useCallback(
    (node) =>
      isNodeDone(node) ? "done" : node?.content ? "in_progress" : "not_started",
    [isNodeDone]
  );
  const sections = useMemo(() => normalizeSections(apiSections), [apiSections]);

  // Flat list of selectable nodes (sections + subsections), in display order.
  const flatNodes = useMemo(() => {
    const list = [];
    sections.forEach((s) => {
      list.push({ ...s, depth: 0 });
      (s.children || []).forEach((c) => list.push({ ...c, depth: 1 }));
    });
    return list;
  }, [sections]);

  // Active section is persisted in localStorage (per project) so a refresh
  // restores it. On first render flatNodes is still empty, so seed from the
  // saved value; the re-seed effect below keeps it if valid, else falls back.
  const { projectId } = useParams();
  const [activeId, setActiveId] = useState(
    () => readSavedActiveId(projectId) || flatNodes[0]?.id
  );
  // Saved selection waits here until the real sections load — on mount flatNodes
  // is still the sample data, which never contains the saved (real) id, so we
  // must not let the re-seed effect fall back to the cover letter meanwhile.
  const pendingRestoreRef = useRef(readSavedActiveId(projectId));

  // Inline title editing. `titleOverrides` holds optimistic titles so a commit
  // shows instantly (pruned once the server catches up, see effect below).
  // `skipCommitRef` lets Escape blur without saving.
  const [editingId, setEditingId] = useState(null);
  const [editingValue, setEditingValue] = useState("");
  const [titleOverrides, setTitleOverrides] = useState({});
  const skipCommitRef = useRef(false);

  // Content map keyed by node id; edits live here so switching sections
  // preserves unsaved work.
  const [contentMap, setContentMap] = useState(() => {
    const map = {};
    flatNodes.forEach((n) => {
      map[n.id] = n.content || "";
    });
    return map;
  });

  // Last known server content per node, to distinguish a fresh server value
  // (e.g. just regenerated) from a local unsaved edit.
  const serverContentRef = useRef({});
  // Bumped when server content changes, so the active editor remounts and picks
  // up new content instead of only updating on section switch.
  const [serverContentVersion, setServerContentVersion] = useState(0);
  const navItemRefs = useRef({});

  // Re-seed when section data changes (API hydrate or regeneration completes).
  useEffect(() => {
    const prevServerMap = serverContentRef.current;
    let changed = false;
    flatNodes.forEach((n) => {
      const prevServer = prevServerMap[n.id];
      if (prevServer !== undefined && n.content && n.content !== prevServer) {
        changed = true;
      }
    });
    setContentMap((prev) => {
      const next = { ...prev };
      flatNodes.forEach((n) => {
        const prevServer = prevServerMap[n.id];
        if (next[n.id] === undefined) {
          next[n.id] = n.content || "";
        } else if (n.content && n.content !== prevServer) {
          // Server content changed (regenerated) — adopt it over any stale edit.
          next[n.id] = n.content;
        }
      });
      return next;
    });
    const newServerMap = {};
    flatNodes.forEach((n) => {
      newServerMap[n.id] = n.content || "";
    });
    serverContentRef.current = newServerMap;
    if (changed) setServerContentVersion((v) => v + 1);
    const hasRealData = Array.isArray(apiSections) && apiSections.length > 0;
    setActiveId((prev) => {
      const pending = pendingRestoreRef.current;
      if (pending) {
        // Saved section has loaded — restore it.
        if (flatNodes.some((n) => n.id === pending)) {
          pendingRestoreRef.current = null;
          return pending;
        }
        // Real data is in but the saved section is gone — give up, fall back.
        if (hasRealData) {
          pendingRestoreRef.current = null;
          return flatNodes[0]?.id;
        }
        // Still loading (sample data) — keep waiting, don't clobber.
        return prev;
      }
      return prev && flatNodes.some((n) => n.id === prev) ? prev : flatNodes[0]?.id;
    });
  }, [flatNodes, apiSections]);

  // Drop optimistic title overrides once server data carries the same title (or
  // the node is gone), so they can't mask later edits.
  useEffect(() => {
    setTitleOverrides((prev) => {
      const keys = Object.keys(prev);
      if (!keys.length) return prev;
      const byId = new Map(flatNodes.map((n) => [n.id, n]));
      let changed = false;
      const next = { ...prev };
      keys.forEach((id) => {
        const node = byId.get(id);
        if (!node || node.title === prev[id]) {
          delete next[id];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [flatNodes]);

  const [isDirty, setIsDirty] = useState(false);
  const [saveStatus, setSaveStatus] = useState("idle"); // idle | saving | saved
  // Survives the editor's remount on node switch (its key is the active node id),
  // carrying the scroll position so a click-to-edit switch doesn't move the page.
  const viewCarryRef = useRef(null);

  const editorRef = useRef(null);

  const activeNode = useMemo(
    () => flatNodes.find((n) => n.id === activeId) || flatNodes[0],
    [flatNodes, activeId]
  );

  // Persist the active section so a refresh lands the user back on the same
  // section/subsection. Only write a value that maps to a real node, so the
  // transient sample-data phase can't clobber the saved selection.
  useEffect(() => {
    if (!activeId || !flatNodes.some((n) => n.id === activeId)) return;
    writeSavedActiveId(projectId, activeId);
  }, [activeId, flatNodes, projectId]);

  // Prefer server content the INSTANT it changes, rather than waiting for the
  // contentMap-hydration effect below (which runs one render after the server
  // data lands). Without this, the moment a generation/regeneration's
  // "completed" frame clears the progress overlay, this node's `content` is
  // already fresh but `contentMap[key]` is still the old (often empty) value
  // for one more render — showing a brief "empty, then it appears" flash.
  const resolveFreshContent = (key, node) => {
    const serverContent = node?.content ?? "";
    const isFresh =
      key != null && serverContent && serverContent !== serverContentRef.current[key];
    return isFresh ? serverContent : contentMap[key] ?? serverContent;
  };

  // Key off the resolved active node so highlight, heading and editor content
  // can never point at different sections.
  const activeKey = activeNode?.id;
  const activeContent = resolveFreshContent(activeKey, activeNode);
  // The heading, the Regenerate / Assign / Mark-as-Done buttons, the knowledge
  // scope pill and the sidebar highlight all describe the node you are EDITING.
  // This used to follow whichever heading was scrolled to the top instead, so the
  // buttons could act on a different section than the one holding your caret.
  const contextNode = activeNode;
  const contextKey = contextNode?.id || activeKey;
  const contextContent = resolveFreshContent(contextKey, contextNode);
  const activeSources = contextNode?.sources || [];
  const [editorScrollToEnd, setEditorScrollToEnd] = useState(false);
  // The editor shows ONE section group — the active node's owning section plus
  // its subsections — not the whole proposal. Only the active node is ever
  // editable, so rendering every section made most of the page look like a
  // document you could type into when it was inert HTML.
  const groupNodes = useMemo(() => {
    const index = flatNodes.findIndex((node) => node.id === activeKey);
    if (index < 0) return [];
    // Walk back to the owning section, then forward over its subsections.
    let start = index;
    while (start > 0 && flatNodes[start].depth === 1) start -= 1;
    let end = start + 1;
    while (end < flatNodes.length && flatNodes[end].depth === 1) end += 1;
    return flatNodes.slice(start, end).map((node) => ({
      ...node,
      title: titleOverrides[node.id] ?? node.title,
      content: contentMap[node.id] ?? node.content ?? "",
    }));
  }, [flatNodes, activeKey, contentMap, titleOverrides]);

  const groupIndex = useMemo(
    () => groupNodes.findIndex((node) => node.id === activeKey),
    [groupNodes, activeKey]
  );
  const precedingNodes = useMemo(
    () => (groupIndex < 0 ? [] : groupNodes.slice(0, groupIndex)),
    [groupNodes, groupIndex]
  );
  const followingNodes = useMemo(
    () => (groupIndex < 0 ? [] : groupNodes.slice(groupIndex + 1)),
    [groupNodes, groupIndex]
  );

  useEffect(() => {
    const el = navItemRefs.current[contextKey];
    if (!el) return;
    el.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [contextKey]);

  // Sources overlay: the referenced-chunk list + the single-source doc viewer.
  const [isSourcesListOpen, setIsSourcesListOpen] = useState(false);
  const [currentSources, setCurrentSources] = useState([]);
  const [isSourceViewOpen, setIsSourceViewOpen] = useState(false);
  const [selectedSourceData, setSelectedSourceData] = useState(null);

  const handleViewSourcesClick = (sources, e) => {
    if (e) e.stopPropagation();
    if (!sources || sources.length === 0) return;
    setCurrentSources(sources);
    setIsSourcesListOpen(true);
  };

  const handleSelectSource = (source) => {
    setSelectedSourceData({
      url: source.file_url,
      name: source.file_name,
      page: source.page_number !== undefined ? source.page_number : source.page,
      highlightText: source.source_chunk || source.snippet,
    });
    setIsSourcesListOpen(false);
    setTimeout(() => setIsSourceViewOpen(true), 150);
  };

  // Scope pill: empty KB + empty project scope ⇒ the whole knowledge base was
  // used. Any KB or project selection ⇒ a custom scope the user can break down.
  const activeKbScope = contextNode?.kbScope || { folders: [], files: [] };
  const activeRfpScope = contextNode?.rfpScope || { folders: [], files: [] };
  const kbScopeCount =
    (activeKbScope.folders?.length || 0) + (activeKbScope.files?.length || 0);
  const rfpScopeCount =
    (activeRfpScope.folders?.length || 0) + (activeRfpScope.files?.length || 0);
  const totalScopeCount = kbScopeCount + rfpScopeCount;
  const kbScopeLabel =
    totalScopeCount === 0 ? "Complete Knowledge Base" : "Custom Scope";
  const kbScopeTooltip =
    totalScopeCount === 0
      ? "DocuSensa used all folders and files available in the knowledge base to generate this section."
      : "This section was generated using only selected knowledge base and project sources.";
  // Only show the pill once the node has generated content.
  const showKbScopePill = Boolean(contextContent);

  // Progress is keyed by the node actually being drafted. A section-family
  // generate registers under the SECTION, yet that one call writes this
  // subsection too — so fall back to the owning section's key. Without it the
  // editor sat idle after clicking Generate on a subsection and only woke up
  // once content landed, because it was watching a key nothing ever wrote to.
  const sectionKey = groupNodes[0]?.targetId;
  const activeGenProgress =
    (contextNode?.targetId ? generatingTargets[contextNode.targetId] : null) ||
    (sectionKey ? generatingTargets[sectionKey] : null) ||
    null;
  const isGeneratingActive = !!activeGenProgress;

  // Generate ALWAYS drafts the whole section. Standing on 6.3 and pressing
  // Generate writes 6, 6.1, 6.2, 6.3 — one backend call covers the family.
  // Regenerate is the scoped counterpart: it rewrites only the selected node.
  //
  // Targeting the section unconditionally is safe because the backend fills only
  // what is blank — it sets write_intro = (section has no response) and takes
  // only still-empty children, so finished text is never overwritten. That also
  // covers a subsection added to an already-drafted section: generating the
  // section writes just the new empty one.
  const generateTarget = useMemo(
    () => groupNodes[0] || activeNode || null,
    [activeNode, groupNodes]
  );

  // The writing task a node was ACTUALLY drafted with, mirroring the backend
  // (rfp/tasks/sections.py — the per-node draft source): a subsection with its
  // own writing brief uses that; otherwise it inherits the SECTION's brief plus
  // its own retrieval query. So a subsection that shipped with the section shows
  // the section-level prompt here, because that is the prompt that wrote it.
  const effectivePrompt = useMemo(() => {
    const node = contextNode;
    if (!node) return "";
    const own = (node.writingBrief || "").trim();
    if (node.depth !== 1) return own || (node.query || "").trim();
    if (own) return own;
    const section = groupNodes[0];
    const sectionBrief = ((section?.writingBrief || section?.query) || "").trim();
    return [sectionBrief, (node.query || "").trim()].filter(Boolean).join("\n\n");
  }, [contextNode, groupNodes]);

  // Tell the parent about the active node (drives the Generate modal title and
  // the step-bar "Mark as Done" button). Assign / Mark-as-Done act on the node
  // you selected; Generate acts on `generateTarget` (see above).
  useEffect(() => {
    if (!onActiveNodeChange) return;
    onActiveNodeChange(
      contextNode
        ? {
            id: contextNode.id,
            targetId: contextNode.targetId,
            title: contextNode.title || "",
            isSubsection: contextNode.depth === 1,
            markAsDone: !!contextNode.markAsDone,
            kbScope: contextNode.kbScope || { folders: [], files: [] },
            rfpScope: contextNode.rfpScope || { folders: [], files: [] },
            assignedMembers: contextNode.assignedMembers || [],
            query: contextNode.query || "",
            // Readable writing task (agentic TOCs) — the Generate/Regenerate
            // modals preview this instead of the retrieval query when present.
            writingBrief: contextNode.writingBrief || "",
            // The brief this node was really written with — section-level for a
            // subsection that inherited it. Shown by the Regenerate modal.
            effectivePrompt,
            // Drives the step bar's Generate → Regenerate switch.
            hasContent: !!contextNode.content,
            // The node Generate should draft, plus the prompt/brief to show for
            // it — so the modal previews the SECTION's writing task when that is
            // what will actually be written.
            generateTarget: generateTarget
              ? {
                  targetId: generateTarget.targetId,
                  title: generateTarget.title || "",
                  isSubsection: generateTarget.depth === 1,
                  query: generateTarget.query || "",
                  // Targeting the section → its own brief. Targeting this node →
                  // the brief it will really be drafted with (section-inherited
                  // when it has none of its own).
                  writingBrief:
                    generateTarget.id === contextNode.id
                      ? effectivePrompt
                      : generateTarget.writingBrief || "",
                  kbScope: generateTarget.kbScope || { folders: [], files: [] },
                  rfpScope: generateTarget.rfpScope || { folders: [], files: [] },
                  // True when Generate will draft the whole section group rather
                  // than just the selected node.
                  coversGroup: generateTarget.id !== contextNode.id,
                }
              : null,
          }
        : null
    );
  }, [contextNode, generateTarget, effectivePrompt, onActiveNodeChange]);

  const handleEditorChange = useCallback(
    (html) => {
      setContentMap((prev) => ({ ...prev, [activeKey]: html }));
      setIsDirty(true);
      if (saveStatus === "saved") setSaveStatus("idle");
    },
    [activeKey, saveStatus]
  );

  // Persist the active node's edited content; the parent converts HTML to
  // Markdown and PATCHes it.
  const handleSave = useCallback(async () => {
    if (!onSave || !activeNode?.targetId) return false;
    const html = editorRef.current?.getContent?.() ?? activeContent;
    setSaveStatus("saving");
    try {
      const ok = await onSave({
        targetId: activeNode.targetId,
        isSubsection: activeNode.depth === 1,
        html,
      });
      if (ok === false) throw new Error("save failed");
      // New server baseline, so a later refetch doesn't remount the editor.
      serverContentRef.current[activeKey] = html;
      setIsDirty(false);
      setSaveStatus("saved");
      return true;
    } catch (e) {
      setSaveStatus("idle");
      return false;
    }
  }, [onSave, activeNode, activeKey, activeContent]);

  // Auto-save: debounce edits and persist without an explicit Save button.
  // Resets on each keystroke (activeContent dep), so it only fires once the
  // user pauses. Nodes without a targetId keep edits in contentMap only.
  useEffect(() => {
    if (!isDirty || !activeNode?.targetId || saveStatus === "saving") return undefined;
    const t = setTimeout(() => {
      handleSave();
    }, 1500);
    return () => clearTimeout(t);
  }, [isDirty, activeContent, activeNode, saveStatus, handleSave]);

  // Ref mirror of the latest save-relevant state. The unmount-flush effect
  // below needs an EMPTY dependency array (it must fire only on true unmount,
  // not on every debounce reset above), so it cannot close over these values
  // directly — it would see whatever they were on first render.
  const saveStateRef = useRef({ isDirty, activeNode, saveStatus, handleSave });
  useEffect(() => {
    saveStateRef.current = { isDirty, activeNode, saveStatus, handleSave };
  }, [isDirty, activeNode, saveStatus, handleSave]);

  // Flush a pending edit when this page unmounts (navigating Draft -> Review,
  // or away from the proposal entirely) instead of silently losing it. The
  // debounce effect above only clearTimeout()s its pending save on unmount —
  // it never sends it — so an edit or image insert made less than 1500ms
  // before navigating away was dropped outright: not "delayed", just never
  // PATCHed at all. This mirrors handleSelectNode's existing flush-before-
  // switch, just for leaving the page rather than switching sections inside it.
  useEffect(() => {
    return () => {
      const s = saveStateRef.current;
      if (s.isDirty && s.activeNode?.targetId && s.saveStatus !== "saving") {
        s.handleSave();
      }
    };
  }, []);

  // Clear the "Saved" confirmation after a few seconds so it doesn't linger.
  useEffect(() => {
    if (saveStatus !== "saved") return undefined;
    const t = setTimeout(() => setSaveStatus("idle"), 3000);
    return () => clearTimeout(t);
  }, [saveStatus]);

  // Switch sections, auto-saving unsaved edits first. If the save fails we keep
  // the user on the current section. Nodes with no targetId just switch (edits
  // stay in the in-memory contentMap).
  const handleSelectNode = useCallback(
    async (newId, scrollToEnd = false) => {
      // On any path that does NOT switch, drop a carried scroll position — the
      // editor won't remount to consume it, and a stale one would suppress the
      // scroll on the next (legitimate) sidebar navigation.
      if (!newId || newId === activeKey) {
        viewCarryRef.current = null;
        return;
      }
      if (isDirty && activeNode?.targetId && saveStatus !== "saving") {
        const ok = await handleSave();
        if (ok === false) {
          viewCarryRef.current = null;
          return;
        }
      }
      setEditorScrollToEnd(scrollToEnd);
      setActiveId(newId);
      setIsDirty(false);
      setSaveStatus("idle");
    },
    [activeKey, isDirty, activeNode, saveStatus, handleSave]
  );

  const doneCount = useMemo(
    () => flatNodes.filter((n) => isNodeDone(n)).length,
    [flatNodes, isNodeDone]
  );

  // Hidden for reviewers (read-only).
  const showMenu = !isReviewer;

  // Optimistic override wins until the server catches up.
  const displayTitle = (node) => titleOverrides[node.id] ?? node.title;

  // Descriptor for the parent's action handlers. For a subsection,
  // `parentTargetId` is its section's id (delete API / assign modal rely on it);
  // `content` carries unsaved edits so "Save to Knowledge Base" persists them.
  const makeNodePayload = useCallback(
    (node, isSub, parentTargetId = null) => ({
      id: node.id,
      targetId: node.targetId,
      title: (titleOverrides[node.id] ?? node.title) || "",   // inlined displayTitle
      isSubsection: isSub,
      hasContent: !!node.content,
      content: contentMap[node.id] ?? node.content ?? "",
      assignedMembers: node.assignedMembers || [],
      kbScope: node.kbScope || { folders: [], files: [] },
      query: node.query || "",
      parentTargetId,
    }),
    [contentMap, titleOverrides]
  );

  const beginEditTitle = (node) => {
    setEditingId(node.id);
    setEditingValue(displayTitle(node) || "");
  };

  const cancelEditTitle = () => {
    setEditingId(null);
    setEditingValue("");
  };

  // Optimistically show the new title, then PATCH it. No-op (empty/unchanged) exits.
  const commitEditTitle = async (node, isSub, parentTargetId) => {
    const newTitle = editingValue.trim();
    const current = displayTitle(node) || "";
    setEditingId(null);
    setEditingValue("");
    if (!newTitle || newTitle === current) return;
    setTitleOverrides((prev) => ({ ...prev, [node.id]: newTitle }));
    const ok = await onUpdateTitle?.(
      makeNodePayload(node, isSub, parentTargetId),
      newTitle
    );
    if (ok === false) {
      // Roll the optimistic title back on failure.
      setTitleOverrides((prev) => {
        const next = { ...prev };
        delete next[node.id];
        return next;
      });
    }
  };

  // Enter → commit, Escape → cancel; both route through blur for a single path.
  const handleEditKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      e.currentTarget.blur();
    } else if (e.key === "Escape") {
      e.preventDefault();
      skipCommitRef.current = true;
      e.currentTarget.blur();
    }
  };

  const handleEditBlur = (node, isSub, parentTargetId) => {
    if (skipCommitRef.current) {
      skipCommitRef.current = false;
      cancelEditTitle();
      return;
    }
    commitEditTitle(node, isSub, parentTargetId);
  };

  // Hover actions for a node in the document. Regenerate and Assign make the
  // node active first — both open UI that reads the active node, and by the time
  // it renders the selection has committed. Mark-done fires immediately, so it
  // takes the node explicitly rather than racing that state update.
  const renderNodeActions = useCallback(
    (node) => {
      if (!node || isReviewer) return null;
      return (
        <NodeActions
          isDone={isNodeDone(node)}
          isBusy={isNodeGenerating(node)}
          // Read through contentMap so a node the user has just typed into
          // counts as written, not as an empty node still needing a first draft.
          hasContent={!!String(contentMap[node.id] ?? node.content ?? "").trim()}
          onGenerate={async () => {
            await handleSelectNode(node.id);
            onGenerateNode?.();
          }}
          onRegenerate={async () => {
            await handleSelectNode(node.id);
            onRegenerateNode?.();
          }}
          onAssign={async () => {
            await handleSelectNode(node.id);
            onAssignNode?.(makeNodePayload(node, node.depth === 1));
          }}
          onMarkDone={() => onMarkNodeDone?.(makeNodePayload(node, node.depth === 1))}
        />
      );
    },
    [
      isReviewer, isNodeDone, isNodeGenerating, handleSelectNode, makeNodePayload,
      contentMap, onGenerateNode, onRegenerateNode, onAssignNode, onMarkNodeDone,
    ]
  );

  // Generate/Assign make the node active first, then defer to the parent.
  const menuHandlers = (node, isSub, parentTargetId = null, parentSection = null) => ({
    onAddSub: () =>
      isSub
        ? onAddSubsection?.(parentSection, node.number)
        : onAddSubsection?.(node, null),
    onEdit: () => beginEditTitle(node),
    onDelete: () => onDeleteNode?.(makeNodePayload(node, isSub, parentTargetId)),
  });

  if (loading) return <DraftLoadingState />;

  return (
    <div className="h-full flex bg-white rounded-l-[10px] border border-[#ECE9EE] shadow-sm overflow-hidden">
      {/* Left: Progress / sections */}
      <aside className="w-[300px] flex-shrink-0 border-r border-gray-200 flex flex-col">
        <div className="flex items-center justify-between px-4 pt-3 pb-1">
          <h2 className="text-[#1A1A1A] font-outfit text-lg font-medium">
            Progress
          </h2>
          <span className="text-[#418360] font-outfit text-[13px] font-normal">
            {doneCount}/{flatNodes.length} Done
          </span>
        </div>
        <div className="mx-4 border-b border-[#ECE9EE]" />
        <div className="px-5 pt-3">
          <p className="text-[#767676] font-outfit text-[13px] font-medium tracking-wide uppercase">
            Proposal Sections
          </p>
        </div>
        <nav className="flex-1 overflow-y-auto custom-scrollbar pl-3 pr-2 py-2 space-y-1 scroll-smooth">
          {sections.map((section, idx) => {
            const sStatus = nodeStatus(section);
            const isActive = section.id === contextKey;
            return (
              <div key={section.id} className="group/sec" ref={(el) => { navItemRefs.current[section.id] = el; }}>
                <div className="group/secrow relative">
                  {editingId === section.id ? (
                    <div
                      className={`w-full flex items-center gap-2 pl-2 pr-2 py-2 rounded-md ${
                        isActive ? "bg-[#F7F7F7]" : "bg-gray-50"
                      }`}
                    >
                      <StatusDot status={sStatus} />
                      {displayNumber(section) && (
                        <span className="font-outfit text-sm text-[#374151] flex-shrink-0">
                          {displayNumber(section)}.
                        </span>
                      )}
                      <input
                        autoFocus
                        value={editingValue}
                        onChange={(e) => setEditingValue(e.target.value)}
                        onFocus={(e) => e.target.select()}
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={handleEditKeyDown}
                        onBlur={() => handleEditBlur(section, false)}
                        // Inline font-size pins the size against global login
                        // CSS that bumps `input:hover/:focus` to 16px.
                        style={{ fontSize: "14px", lineHeight: "20px", fontWeight: 400 }}
                        className="flex-1 min-w-0 bg-white border border-[#A04A97] rounded px-1.5 py-0.5 font-outfit text-[#374151] outline-none focus:ring-1 focus:ring-[#A04A97]"
                      />
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleSelectNode(section.id)}
                      className={`w-full flex items-center gap-2 pl-2 pr-9 py-2 rounded-md text-left transition-colors ${
                        isActive ? "bg-[#F7F7F7]" : "hover:bg-gray-50"
                      }`}
                    >
                      {isNodeGenerating(section) ? (
                        <MiniSpinner />
                      ) : (
                        <StatusDot status={sStatus} />
                      )}
                      <span
                        style={{ color: isActive ? "#A04A97" : "#374151" }}
                        className={`font-outfit text-sm truncate ${
                          isActive ? "font-medium" : ""
                        }`}
                      >
                        {displayNumber(section)
                          ? `${displayNumber(section)}. `
                          : ""}
                        {displayTitle(section)}
                      </span>
                    </button>
                  )}
                  {showMenu && editingId !== section.id && (
                    <DraftActionMenu
                      isSubsection={false}
                      canAddSub={!!formatNumber(section.number)}
                      triggerClassName="absolute right-1.5 top-1/2 -translate-y-1/2 w-7 h-7 opacity-0 group-hover/secrow:opacity-100"
                      {...menuHandlers(section, false)}
                    />
                  )}
                </div>
                {(section.children || []).map((sub) => {
                  const subActive = sub.id === contextKey;
                  return (
                    <div key={sub.id} className="group/subrow relative" ref={(el) => { navItemRefs.current[sub.id] = el; }}>
                      {editingId === sub.id ? (
                        <div
                          className={`w-full flex items-center gap-2 pl-7 pr-2 py-1.5 rounded-md ${
                            subActive ? "bg-[#F7F7F7]" : "bg-gray-50"
                          }`}
                        >
                          <StatusDot status={nodeStatus(sub)} />
                          {displayNumber(sub) && (
                            <span className="font-outfit text-[13px] text-[#6B7280] flex-shrink-0">
                              {displayNumber(sub)}
                            </span>
                          )}
                          <input
                            autoFocus
                            value={editingValue}
                            onChange={(e) => setEditingValue(e.target.value)}
                            onFocus={(e) => e.target.select()}
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={handleEditKeyDown}
                            onBlur={() => handleEditBlur(sub, true, section.targetId)}
                            // Pins size against global login CSS that bumps input:hover/:focus to 16px.
                            style={{ fontSize: "13px", lineHeight: "18px", fontWeight: 400 }}
                            className="flex-1 min-w-0 bg-white border border-[#A04A97] rounded px-1.5 py-0.5 font-outfit text-[#374151] outline-none focus:ring-1 focus:ring-[#A04A97]"
                          />
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleSelectNode(sub.id)}
                          className={`w-full flex items-center gap-2 pl-7 pr-9 py-1.5 rounded-md text-left transition-colors ${
                            subActive ? "bg-[#F7F7F7]" : "hover:bg-gray-50"
                          }`}
                        >
                          {isNodeGenerating(sub) ? (
                            <MiniSpinner />
                          ) : (
                            <StatusDot status={nodeStatus(sub)} />
                          )}
                          <span
                            style={{ color: subActive ? "#A04A97" : "#6B7280" }}
                            className={`font-outfit text-[13px] truncate ${
                              subActive ? "font-medium" : ""
                            }`}
                          >
                            {displayNumber(sub)
                              ? `${displayNumber(sub)} `
                              : ""}
                            {displayTitle(sub)}
                          </span>
                        </button>
                      )}
                      {showMenu && editingId !== sub.id && (
                        <DraftActionMenu
                          isSubsection
                          canAddSub={!!formatNumber(sub.number)}
                          triggerClassName="absolute right-1.5 top-1/2 -translate-y-1/2 w-7 h-7 opacity-0 group-hover/subrow:opacity-100"
                          {...menuHandlers(sub, true, section.targetId, section)}
                        />
                      )}
                    </div>
                  );
                })}
                <AddSectionDivider
                  onAdd={
                    onAddSectionAt ? () => onAddSectionAt(idx + 1) : undefined
                  }
                />
              </div>
            );
          })}
        </nav>
      </aside>

      {/* Right: Editor */}
      <section className="flex-1 min-w-0 flex flex-col">
        <div className="flex items-start justify-between gap-4 px-6 pt-5">
          <div className="flex items-center gap-3 min-w-0">
            <AssignedAvatars members={contextNode?.assignedMembers || []} />
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <span
              className={`font-outfit text-[13px] font-medium ${
                saveStatus === "saving"
                  ? "text-[#A04A97]"
                  : isDirty
                  ? "text-[#9CA3AF]"
                  : saveStatus === "saved"
                  ? "text-[#418360]"
                  : "text-transparent"
              }`}
            >
              {saveStatus === "saving"
                ? "Saving…"
                : isDirty
                ? "Unsaved changes"
                : saveStatus === "saved"
                ? "Saved"
                : ""}
            </span>
          </div>
        </div>

        {/* Knowledge scope chip — reflects the node's kb_scope once generated */}
        {showKbScopePill && (
          <div className="px-6 pt-3 flex items-center gap-2">
            <span className="text-[#374151] font-outfit text-sm">
              Knowledge Scope:
            </span>
            <KbScopePill
              kbScope={activeKbScope}
              rfpScope={activeRfpScope}
              label={kbScopeLabel}
              tooltip={kbScopeTooltip}
              hasCustomScope={totalScopeCount > 0}
              kbHasScope={kbScopeCount > 0}
              nodeKey={contextKey}
            />

            {activeSources.length > 0 && (
              <>
                <span className="text-[#000000B3] font-outfit text-sm ml-4">
                  Sources:
                </span>
                <button
                  onClick={(e) => handleViewSourcesClick(activeSources, e)}
                  className="text-[#A04A97] font-outfit text-[13px] font-medium underline cursor-pointer"
                >
                  View Sources ({activeSources.length})
                </button>
              </>
            )}
          </div>
        )}

        {/* Editor (same component as the Proposal Analysis page) */}
        <div className="flex-1 min-h-0 px-6 py-4">
          <div className="relative h-full border border-gray-200 rounded-lg overflow-hidden flex flex-col">
            {isGeneratingActive &&
              !activeGenProgress?.streamText &&
              !!contextContent && (
                // Rewrite mode — the banner sits ABOVE the editor in normal
                // flow (no overlap); the user's current text stays fully
                // readable below it until the new draft lands.
                <div className="bg-white border-b border-gray-100 shadow-sm px-4 py-3 flex flex-col gap-2 shrink-0">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className="animate-spin rounded-full h-[18px] w-[18px] border-b-2 border-[#A04A97] shrink-0" />
                    <GenStatusMessage progress={activeGenProgress} />
                  </div>
                  <GenStepTracker percent={activeGenProgress?.percent} />
                  <p className="text-[13px] text-[#6B7280] font-outfit">
                    Rewriting this section — your current version stays until
                    the new draft is ready. Long sections can take 1–2 minutes.
                  </p>
                </div>
              )}
            <div
              className={`flex-1 min-h-0 ${
                isGeneratingActive &&
                !activeGenProgress?.streamText &&
                contextContent
                  ? "opacity-60 pointer-events-none"
                  : ""
              }`}
            >
              <ProposalEditor
                key={`${activeKey}:${serverContentVersion}`}
                ref={editorRef}
                initialContent={activeContent}
                onChange={handleEditorChange}
                folderId={folderId}
                scrollToEnd={editorScrollToEnd}
                title={activeNode?.title || "Section"}
                number={activeNode?.number}
                depth={activeNode?.depth ?? 0}
                precedingNodes={precedingNodes}
                followingNodes={followingNodes}
                nodeId={activeKey}
                scrollToNodeId={activeKey}
                // Clicking a sibling in the group hands it the editor, saving
                // any pending edits first (same path as the sidebar).
                onSelectNode={isReviewer ? undefined : handleSelectNode}
                viewCarry={viewCarryRef}
                nodeActionsFor={isReviewer ? undefined : renderNodeActions}
                activeNodeActions={isReviewer ? undefined : renderNodeActions(activeNode)}
              />
            </div>
            {isGeneratingActive &&
              (activeGenProgress?.streamText ? (
                // Once the LLM starts writing, replace everything with the
                // response streaming in live.
                <div className="absolute inset-0 z-10 flex flex-col bg-white">
                  <div className="flex items-center gap-2.5 px-4 py-2.5 border-b border-gray-100 shrink-0">
                    <div className="animate-spin rounded-full h-[18px] w-[18px] border-b-2 border-[#A04A97]" />
                    <span className="text-[#3F3F46] font-outfit text-[14px] font-medium">
                      Writing…
                    </span>
                    <span className="ml-auto text-[#A04A97] font-outfit text-[13px] font-medium tabular-nums">
                      {
                        cleanStreamText(activeGenProgress.streamText)
                          .split(/\s+/)
                          .filter(Boolean).length
                      }{" "}
                      words
                    </span>
                  </div>
                  <StreamingPreview text={activeGenProgress.streamText} />
                </div>
              ) : !contextContent ? (
                // Fresh generation. The node itself is empty, but the group's
                // other headings sit behind this overlay — at bg-white/80 over a
                // 1px blur they stayed legible and tangled with the progress
                // text. Real blur on the backdrop, and the status moves onto its
                // own SOLID card so foreground and background can never mix
                // however dark the content behind happens to be.
                <div className="absolute inset-0 z-10 flex items-center justify-center px-8 bg-white/55 backdrop-blur-[5px]">
                  <div className="flex flex-col items-center gap-3 max-w-[440px] rounded-2xl border border-[#ECE9EE] bg-white px-8 py-7 shadow-[0_18px_50px_-20px_rgba(32,10,28,0.35)]">
                    <div className="animate-spin rounded-full h-9 w-9 border-b-2 border-[#A04A97]" />
                    <GenStatusMessage progress={activeGenProgress} center />
                    <GenStepTracker percent={activeGenProgress?.percent} />
                    <p className="text-[13px] text-[#6B7280] font-outfit">
                      Long sections can take 1–2 minutes.
                    </p>
                  </div>
                </div>
              ) : null)}
          </div>
        </div>
      </section>

      <SourcesListModal
        isOpen={isSourcesListOpen}
        onClose={() => setIsSourcesListOpen(false)}
        sources={currentSources}
        onSelectSource={handleSelectSource}
      />

      <SourceViewModal
        isOpen={isSourceViewOpen}
        onClose={() => setIsSourceViewOpen(false)}
        data={selectedSourceData}
      />
    </div>
  );
};

export default ProposalDraftStep;
